import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { purchaseOrderService } from "services";
import {
  PurchaseOrderForm,
  PurchaseOrderQuery,
  PurchaseOrderUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type PurchaseOrderListKey =
  | ["purchase-orders"]
  | ["purchase-orders", PurchaseOrderQuery];
type PurchaseOrderDetailKey = ["purchase-orders", "detail", number | string];

const purchaseOrderKeys = {
  all: ["purchase-orders"] as const,
  list: (params?: PurchaseOrderQuery): PurchaseOrderListKey =>
    params ? ["purchase-orders", params] : ["purchase-orders"],
  detail: (id: number | string): PurchaseOrderDetailKey => [
    "purchase-orders",
    "detail",
    id,
  ],
};

export function usePurchaseOrder() {
  const queryClient = useQueryClient();

  const fetchList = (params?: PurchaseOrderQuery, enabled = true) => {
    return useQuery({
      queryKey: purchaseOrderKeys.list(params),
      queryFn: () => purchaseOrderService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: purchaseOrderKeys.detail(id),
      queryFn: () => purchaseOrderService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createPurchaseOrder = useMutation({
    mutationFn: (payload: PurchaseOrderForm) =>
      purchaseOrderService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo chứng từ mua hàng thành công");
      }
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo chứng từ mua hàng thất bại");
      console.error("createPurchaseOrder failed", err);
    },
  });

  const updatePurchaseOrder = useMutation({
    mutationFn: (payload: PurchaseOrderUpdatePayload) =>
      purchaseOrderService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Cập nhật chứng từ mua hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật chứng từ mua hàng thất bại");
      console.error("updatePurchaseOrder failed", err);
    },
  });

  const deletePurchaseOrder = useMutation({
    mutationFn: (id: number | string) => purchaseOrderService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Xoá chứng từ mua hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá chứng từ mua hàng thất bại");
      console.error("deletePurchaseOrder failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createPurchaseOrder,
    updatePurchaseOrder,
    deletePurchaseOrder,
  };
}
